import { useCallback, useMemo, useRef, useState } from 'react';

import { createWidget, publishWidget, updateWidget } from '../../../services';
import { toUpdatePayload } from '../utils/updatePayload.js';
import { buildWidgetPayload } from '../utils/buildWidgetPayload.js';

/**
 * Persist the builder's widget (CT-5200).
 *
 * Create or update is chosen by whether a widget id is already in hand — the
 * post-save `Keep editing` action hands one back, which is what turns the
 * builder from "creating" into "editing" without a navigation.
 *
 * `submittingRef` guards the double-submit: a second call while a save is in
 * flight is a no-op, so a double-click cannot create two widgets.
 *
 * **Save publishes — and a rejected publish is still a save.** The backend
 * creates every widget as a `DRAFT` and only `/widget/publish` promotes it;
 * the builder's single affordance is `Save to catalog`, so the publish rides
 * along. When it is rejected the widget is already stored, so the save reports
 * success and carries the reason back as `publishError` — reporting failure
 * would lose the uuid and make the user's retry create a duplicate.
 *
 * `versionStampRef` carries the optimistic-concurrency token between writes:
 * the stamp the last write returned is what the next `update` sends as
 * `expectedVersionStamp`, so a second tab editing the same widget gets
 * `DSW011` instead of silently overwriting this one. The token is opaque.
 *
 * `columns` is the loaded column metadata: the payload reads each field's
 * alias, stored type and origin from it.
 *
 * `typeSchema` is the selected presentation type's `configSchema`: it says
 * which binding slots the author was asked about, so the payload can tell a
 * complete contract from a half-answered one.
 *
 * @param {{ widgetId?: string, columns?: Array, typeSchema?: object|null }} [options]
 */
export const useSaveWidget = ({ widgetId, columns, typeSchema, aggregateLabel } = {}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);
  const versionStampRef = useRef(null);
  /*
    The same failure the `error` state holds, readable in the tick the save
    returns. A caller that wants to *say* something about a rejection — the
    builder's toast tells a locked data source apart from a retryable
    failure (`savedWidgetLock`) — runs immediately after `await save(...)`,
    where the state it just set has not re-rendered yet.
  */
  const errorRef = useRef(null);

  const save = useCallback(
    async (form) => {
      if (submittingRef.current) return null;
      submittingRef.current = true;
      setIsSaving(true);
      errorRef.current = null;
      setError(null);

      try {
        const payload = buildWidgetPayload(form, { columns, typeSchema, aggregateLabel });
        // Every update carries `dataSource` now — see `toUpdatePayload`.
        const updatePayload = toUpdatePayload(payload);
        // The services validate the envelope and map the row themselves, so a
        // rejected save arrives here as a throw, not as an error envelope.
        const written = widgetId
          ? await updateWidget(widgetId, updatePayload, {
            expectedVersionStamp: versionStampRef.current,
          })
          : await createWidget(payload);
        versionStampRef.current = written.versionStamp;

        // The widget exists from here on, whatever publishing does.
        //
        // A rejected publish is not a failed save: `DSW012` means the draft is
        // stored and could not be promoted — today usually
        // `UI_WIDGET_QUERY_NOT_PUBLISHED`, because the underlying query has to
        // be published first and nothing in this builder can do that. Throwing
        // would return `null`, leave the builder without the uuid, and turn
        // the user's retry into a *second* widget, so the reason is carried
        // back instead of raised.
        let published = null;
        let publishError = null;
        if (written.id) {
          try {
            published = await publishWidget(written.id, {
              expectedVersionStamp: written.versionStamp,
            });
          } catch (caught) {
            publishError = caught;
          }
        }
        if (published?.versionStamp) versionStampRef.current = published.versionStamp;

        // The caller renders the saved widget's name — which the write result
        // does not echo back — so the identity just sent is merged in.
        return {
          ...written,
          ...(published ?? {}),
          id: published?.id || written.id,
          name: payload.name,
          description: payload.description,
          // `null` when it published; the caller can say "saved as a draft".
          publishError,
        };
      } catch (caught) {
        // Rethrowing would lose the message the dialog footer needs; the caller
        // reads `error` and keeps every form value in place.
        errorRef.current = caught;
        setError(caught);
        return null;
      } finally {
        submittingRef.current = false;
        setIsSaving(false);
      }
    },
    [aggregateLabel, columns, typeSchema, widgetId],
  );

  const clearError = useCallback(() => {
    errorRef.current = null;
    setError(null);
  }, []);

  return useMemo(
    () => ({ save, isSaving, error, errorRef, clearError }),
    [clearError, error, isSaving, save],
  );
};

export default useSaveWidget;
